import { useState, useEffect, useCallback, useRef } from 'react';
import { GxFeedItem, PaginatedFeedResponse, getFeed } from '../api/guestExperience';

interface UseGxFeedResult {
  items: GxFeedItem[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  loadMore: () => void;
  hasMore: boolean;
}

export function useGxFeed(eventId: string, zoneFilter?: string[]): UseGxFeedResult {
  const [items, setItems] = useState<GxFeedItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const cursorRef = useRef<string | null>(null);
  const isLoadingMoreRef = useRef(false);
  // Serialize zone filter to avoid infinite re-render loops from array reference changes
  const zoneFilterKey = zoneFilter ? zoneFilter.join(',') : '';

  const fetchFeed = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const zones = zoneFilterKey ? zoneFilterKey.split(',') : undefined;
      const response: PaginatedFeedResponse = await getFeed(eventId, {
        zoneIds: zones,
      });
      setItems(response.items);
      cursorRef.current = response.nextCursor;
      setHasMore(response.nextCursor !== null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
  }, [eventId, zoneFilterKey]);

  useEffect(() => {
    fetchFeed();
  }, [fetchFeed]);

  const loadMore = useCallback(async () => {
    if (!cursorRef.current || isLoadingMoreRef.current) return;
    isLoadingMoreRef.current = true;
    try {
      const zones = zoneFilterKey ? zoneFilterKey.split(',') : undefined;
      const response: PaginatedFeedResponse = await getFeed(eventId, {
        cursor: cursorRef.current,
        zoneIds: zones,
      });
      setItems((prev) => [...prev, ...response.items]);
      cursorRef.current = response.nextCursor;
      setHasMore(response.nextCursor !== null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      isLoadingMoreRef.current = false;
    }
  }, [eventId, zoneFilterKey]);

  return { items, isLoading, error, refetch: fetchFeed, loadMore, hasMore };
}
